import React, { useState, useEffect } from 'react';
import { plansApi } from '../../api/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Copy,
  Check,
  X,
  DollarSign,
  Users,
  Clock,
  HardDrive,
  Mail,
  Star,
  Eye,
  EyeOff,
  RefreshCw,
  GripVertical,
  FolderPlus
} from 'lucide-react';
import { toast } from 'sonner';

const defaultPlan = {
  name: '',
  description: '',
  monthly_price: 0,
  yearly_price: 0,
  features: [],
  storage_limit_gb: 5,
  emails_per_day: 100,
  custom_folders_limit: 5,
  wall_storage_gb: 0,
  free_trial_enabled: false,
  free_trial_days: 7,
  is_active: true,
  is_featured: false,
  visible_on_frontend: true,
  sort_order: 0
};

const defaultFeature = { name: '', included: true, limit: '' };

export default function PlansManager() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(true);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState(defaultPlan);
  const [newFeature, setNewFeature] = useState(defaultFeature);
  const [saving, setSaving] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const data = await plansApi.getAll(showInactive);
      setPlans(data);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [showInactive]);

  const openCreateDialog = () => {
    setFormData({
      ...defaultPlan,
      sort_order: plans.length
    });
    setSelectedPlan(null);
    setIsCreating(true);
    setEditDialogOpen(true);
  };

  const openEditDialog = (plan) => {
    setFormData({
      name: plan.name,
      description: plan.description,
      monthly_price: plan.monthly_price,
      yearly_price: plan.yearly_price,
      features: plan.features || [],
      storage_limit_gb: plan.storage_limit_gb,
      emails_per_day: plan.emails_per_day,
      custom_folders_limit: plan.custom_folders_limit || 5,
      wall_storage_gb: plan.wall_storage_gb || 0,
      free_trial_enabled: plan.free_trial_enabled,
      free_trial_days: plan.free_trial_days,
      is_active: plan.is_active,
      is_featured: plan.is_featured,
      visible_on_frontend: plan.visible_on_frontend !== false,
      sort_order: plan.sort_order
    });
    setSelectedPlan(plan);
    setIsCreating(false);
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Plan name is required');
      return;
    }

    setSaving(true);
    try {
      if (isCreating) {
        await plansApi.create(formData);
        toast.success('Plan created successfully');
      } else {
        await plansApi.update(selectedPlan.id, formData);
        toast.success('Plan updated successfully');
      }
      setEditDialogOpen(false);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPlan) return;
    
    try {
      await plansApi.delete(selectedPlan.id);
      toast.success('Plan deleted successfully');
      setDeleteDialogOpen(false);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete plan');
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await plansApi.toggleActive(plan.id);
      toast.success(`Plan ${plan.is_active ? 'deactivated' : 'activated'}`);
      fetchPlans();
    } catch (err) {
      toast.error('Failed to toggle plan status');
    }
  };

  const handleDuplicate = async (plan) => {
    try {
      await plansApi.duplicate(plan.id);
      toast.success('Plan duplicated successfully');
      fetchPlans();
    } catch (err) {
      toast.error('Failed to duplicate plan');
    }
  };

  const addFeature = () => {
    if (!newFeature.name.trim()) return;
    setFormData({
      ...formData,
      features: [...formData.features, { ...newFeature }]
    });
    setNewFeature(defaultFeature);
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const updateFeature = (index, field, value) => {
    const updated = [...formData.features];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, features: updated });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  return (
    <Card className="bg-[#1a1a2e] border-[#2d2d44]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#6D4AFF]" />
              Pricing Plans
            </CardTitle>
            <CardDescription className="text-gray-400">
              Create and manage subscription plans
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={showInactive}
                onCheckedChange={setShowInactive}
                id="show-inactive"
              />
              <Label htmlFor="show-inactive" className="text-sm text-gray-400">
                Show inactive
              </Label>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchPlans}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={openCreateDialog}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Plan
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative p-4 rounded-lg border transition-all ${
                plan.is_active 
                  ? 'bg-[#252540] border-[#2d2d44] hover:border-[#6D4AFF]' 
                  : 'bg-[#1a1a2e] border-[#2d2d44] opacity-60'
              }`}
            >
              {/* Status badges */}
              <div className="absolute top-3 right-3 flex gap-2">
                {plan.is_featured && (
                  <Badge className="bg-yellow-500/20 text-yellow-500">
                    <Star className="w-3 h-3 mr-1" />
                    Featured
                  </Badge>
                )}
                {plan.free_trial_enabled && (
                  <Badge className="bg-green-500/20 text-green-500">
                    <Clock className="w-3 h-3 mr-1" />
                    {plan.free_trial_days}d Trial
                  </Badge>
                )}
                {plan.visible_on_frontend === false && (
                  <Badge className="bg-orange-500/20 text-orange-400">
                    <EyeOff className="w-3 h-3 mr-1" />
                    Hidden
                  </Badge>
                )}
                <Badge className={plan.is_active ? 'bg-green-600' : 'bg-gray-600'}>
                  {plan.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              {/* Plan name & description */}
              <h3 className="text-lg font-semibold text-white mt-6">{plan.name}</h3>
              <p className="text-sm text-gray-400 mt-1 line-clamp-2">{plan.description}</p>

              {/* Pricing */}
              <div className="mt-4 space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-white">{formatPrice(plan.monthly_price)}</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <div className="text-sm text-gray-400">
                  or {formatPrice(plan.yearly_price)}/year
                  {plan.yearly_price < plan.monthly_price * 12 && (
                    <span className="text-green-500 ml-2">
                      (Save {Math.round((1 - plan.yearly_price / (plan.monthly_price * 12)) * 100)}%)
                    </span>
                  )}
                </div>
              </div>

              {/* Limits */}
              <div className="mt-4 flex flex-wrap gap-3 text-sm text-gray-400">
                <div className="flex items-center gap-1">
                  <HardDrive className="w-4 h-4" />
                  {plan.storage_limit_gb}GB
                </div>
                <div className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {plan.emails_per_day}/day
                </div>
                <div className="flex items-center gap-1">
                  <FolderPlus className="w-4 h-4" />
                  {plan.custom_folders_limit || 5} folders
                </div>
                {plan.wall_storage_gb > 0 && (
                  <div className="flex items-center gap-1 text-[#6D4AFF]">
                    <span className="text-xs">📰</span>
                    {plan.wall_storage_gb}GB Wall
                  </div>
                )}
              </div>

              {/* Features preview */}
              {plan.features && plan.features.length > 0 && (
                <div className="mt-3 space-y-1">
                  {plan.features.slice(0, 3).map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {feature.included ? (
                        <Check className="w-3 h-3 text-green-500" />
                      ) : (
                        <X className="w-3 h-3 text-red-500" />
                      )}
                      <span className={feature.included ? 'text-gray-300' : 'text-gray-500'}>
                        {feature.name}
                        {feature.limit && <span className="text-gray-500"> ({feature.limit})</span>}
                      </span>
                    </div>
                  ))}
                  {plan.features.length > 3 && (
                    <p className="text-xs text-gray-500">+{plan.features.length - 3} more features</p>
                  )}
                </div>
              )}

              {/* Subscribers */}
              <div className="mt-4 pt-3 border-t border-[#2d2d44] flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Users className="w-4 h-4" />
                  {plan.subscriber_count} subscribers
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#1a1a2e] border-[#2d2d44]">
                    <DropdownMenuItem
                      className="text-gray-300 focus:bg-[#2d2d44]"
                      onClick={() => openEditDialog(plan)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Plan
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-gray-300 focus:bg-[#2d2d44]"
                      onClick={() => handleDuplicate(plan)}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-gray-300 focus:bg-[#2d2d44]"
                      onClick={() => handleToggleActive(plan)}
                    >
                      {plan.is_active ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-2" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Activate
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#2d2d44]" />
                    <DropdownMenuItem
                      className="text-red-400 focus:bg-[#2d2d44]"
                      onClick={() => {
                        setSelectedPlan(plan);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Plan
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}

          {/* Empty state */}
          {!loading && plans.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-500">
              <DollarSign className="w-12 h-12 mb-4 text-gray-600" />
              <p className="text-lg font-medium">No plans yet</p>
              <p className="text-sm">Create your first pricing plan to get started</p>
              <Button
                onClick={openCreateDialog}
                className="mt-4 bg-[#6D4AFF] hover:bg-[#5B3FD9]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Plan
              </Button>
            </div>
          )}
        </div>
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-2xl max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle className="text-white">
              {isCreating ? 'Create New Plan' : `Edit Plan: ${selectedPlan?.name}`}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {isCreating ? 'Set up a new subscription plan' : 'Update plan details and pricing'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6">
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Plan Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Pro Plan"
                      className="bg-[#252540] border-[#2d2d44] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Sort Order</Label>
                    <Input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                      className="bg-[#252540] border-[#2d2d44] text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what this plan offers..."
                    className="bg-[#252540] border-[#2d2d44] text-white min-h-[80px]"
                  />
                </div>
              </div>

              <Separator className="bg-[#2d2d44]" />

              {/* Pricing */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Pricing</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Monthly Price ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.monthly_price}
                      onChange={(e) => setFormData({ ...formData, monthly_price: parseFloat(e.target.value) || 0 })}
                      className="bg-[#252540] border-[#2d2d44] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Yearly Price ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.yearly_price}
                      onChange={(e) => setFormData({ ...formData, yearly_price: parseFloat(e.target.value) || 0 })}
                      className="bg-[#252540] border-[#2d2d44] text-white"
                    />
                    {formData.yearly_price > 0 && formData.monthly_price > 0 && (
                      <p className="text-xs text-gray-500">
                        {formData.yearly_price < formData.monthly_price * 12 
                          ? `${Math.round((1 - formData.yearly_price / (formData.monthly_price * 12)) * 100)}% savings vs monthly`
                          : 'No savings vs monthly'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <Separator className="bg-[#2d2d44]" />

              {/* Limits */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Limits</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Storage Limit (GB)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={formData.storage_limit_gb}
                      onChange={(e) => setFormData({ ...formData, storage_limit_gb: parseFloat(e.target.value) || 0 })}
                      className="bg-[#252540] border-[#2d2d44] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Emails per Day</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.emails_per_day}
                      onChange={(e) => setFormData({ ...formData, emails_per_day: parseInt(e.target.value) || 0 })}
                      className="bg-[#252540] border-[#2d2d44] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Custom Folders</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.custom_folders_limit}
                      onChange={(e) => setFormData({ ...formData, custom_folders_limit: parseInt(e.target.value) || 0 })}
                      className="bg-[#252540] border-[#2d2d44] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300 flex items-center gap-1">
                      Wall Storage (GB)
                      <span className="text-xs text-[#6D4AFF]">NEW</span>
                    </Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={formData.wall_storage_gb}
                      onChange={(e) => setFormData({ ...formData, wall_storage_gb: parseFloat(e.target.value) || 0 })}
                      className="bg-[#252540] border-[#2d2d44] text-white"
                      placeholder="0 = No wall access"
                    />
                  </div>
                </div>
              </div>

              <Separator className="bg-[#2d2d44]" />

              {/* Free Trial */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Free Trial</h4>
                <div className="flex items-center justify-between p-4 bg-[#252540] rounded-lg">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#6D4AFF]" />
                    <div>
                      <p className="text-white font-medium">Enable Free Trial</p>
                      <p className="text-sm text-gray-400">Allow users to try this plan for free</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.free_trial_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, free_trial_enabled: checked })}
                  />
                </div>
                {formData.free_trial_enabled && (
                  <div className="space-y-2">
                    <Label className="text-gray-300">Trial Duration (days)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={formData.free_trial_days}
                      onChange={(e) => setFormData({ ...formData, free_trial_days: parseInt(e.target.value) || 7 })}
                      className="bg-[#252540] border-[#2d2d44] text-white w-32"
                    />
                  </div>
                )}
              </div>

              <Separator className="bg-[#2d2d44]" />

              {/* Features */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Features</h4>
                
                {/* Existing features */}
                {formData.features.length > 0 && (
                  <div className="space-y-2">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-[#252540] rounded-lg">
                        <GripVertical className="w-4 h-4 text-gray-500" />
                        <Switch
                          checked={feature.included}
                          onCheckedChange={(checked) => updateFeature(index, 'included', checked)}
                        />
                        <Input
                          value={feature.name}
                          onChange={(e) => updateFeature(index, 'name', e.target.value)}
                          placeholder="Feature name"
                          className="flex-1 bg-[#1a1a2e] border-[#2d2d44] text-white h-8"
                        />
                        <Input
                          value={feature.limit || ''}
                          onChange={(e) => updateFeature(index, 'limit', e.target.value)}
                          placeholder="Limit (optional)"
                          className="w-32 bg-[#1a1a2e] border-[#2d2d44] text-white h-8"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFeature(index)}
                          className="h-8 w-8 text-gray-400 hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new feature */}
                <div className="flex items-center gap-2 p-2 border border-dashed border-[#2d2d44] rounded-lg">
                  <Switch
                    checked={newFeature.included}
                    onCheckedChange={(checked) => setNewFeature({ ...newFeature, included: checked })}
                  />
                  <Input
                    value={newFeature.name}
                    onChange={(e) => setNewFeature({ ...newFeature, name: e.target.value })}
                    placeholder="New feature name"
                    className="flex-1 bg-[#252540] border-[#2d2d44] text-white h-8"
                    onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                  />
                  <Input
                    value={newFeature.limit}
                    onChange={(e) => setNewFeature({ ...newFeature, limit: e.target.value })}
                    placeholder="Limit"
                    className="w-32 bg-[#252540] border-[#2d2d44] text-white h-8"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={addFeature}
                    className="h-8 w-8 text-[#6D4AFF]"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <Separator className="bg-[#2d2d44]" />

              {/* Status */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">Status & Visibility</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center justify-between p-4 bg-[#252540] rounded-lg">
                    <div>
                      <p className="text-white font-medium">Active</p>
                      <p className="text-xs text-gray-400">Plan can be assigned to users</p>
                    </div>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-[#252540] rounded-lg">
                    <div className="flex items-center gap-3">
                      <Eye className="w-5 h-5 text-[#6D4AFF]" />
                      <div>
                        <p className="text-white font-medium">Visible on Frontend</p>
                        <p className="text-xs text-gray-400">Show on landing page & pricing section</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.visible_on_frontend}
                      onCheckedChange={(checked) => setFormData({ ...formData, visible_on_frontend: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-[#252540] rounded-lg">
                    <div>
                      <p className="text-white font-medium">Featured</p>
                      <p className="text-xs text-gray-400">Highlight this plan with special styling</p>
                    </div>
                    <Switch
                      checked={formData.is_featured}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                    />
                  </div>
                </div>
                {formData.is_active && !formData.visible_on_frontend && (
                  <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <p className="text-sm text-orange-400">
                      <EyeOff className="w-4 h-4 inline mr-2" />
                      This plan is active but hidden from the frontend. Only admins can assign it to users.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t border-[#2d2d44] px-6 py-4">
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#6D4AFF] hover:bg-[#5B3FD9]">
              {saving ? 'Saving...' : isCreating ? 'Create Plan' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44]">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Plan</DialogTitle>
            <DialogDescription className="text-gray-400">
              Are you sure you want to delete "{selectedPlan?.name}"? 
              {selectedPlan?.subscriber_count > 0 && (
                <span className="text-red-400 block mt-2">
                  This plan has {selectedPlan.subscriber_count} active subscribers and cannot be deleted.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)} className="text-gray-400">
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              variant="destructive"
              disabled={selectedPlan?.subscriber_count > 0}
            >
              Delete Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
